import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { RoutePaths } from "../../../../enumStrings";
import {
  CmpInvoiceAccountingExportProfileEnum,
  CmpOperationalSourceKindEnum,
  CmpPaymentChoiceTypeEnum,
  useCreateCmpInvoiceMutation,
  useDownloadCmpInvoicePdfMutation,
  useExportCmpInvoicesAccountingCsvMutation,
  useGetChamberMemberProSettingsQuery,
  useGetCmpInvoiceDetailQuery,
  useRecordCmpInvoiceManualPaymentMutation,
  useRecordCmpInvoiceManualRefundMutation,
  useRefundCmpInvoiceStripePaymentMutation,
  useResendCmpInvoicePaymentRequestMutation,
  useSendCmpInvoicePaymentRequestMutation,
  useStartCmpInvoiceCardCheckoutMutation,
  useVoidCmpInvoiceMutation,
} from "../../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { getCmpCreditCardPaymentsReady } from "../../../../utils/cmpCreditCardPayments";
import { FormPageShell } from "../../../common/dashboard/FormPageShell";
import UnsavedChangesPrompt from "../../../common/UnsavedChangesPrompt";
import {
  InvoiceEmailRoutingPanel,
  InvoiceHistory,
  InvoiceLineItemsEditor,
  InvoicePaymentMethodSelect,
  InvoiceReadinessAlert,
  InvoiceSection,
  InvoiceStateNotice,
  InvoiceTotals,
  cpaGold,
  cpaGoldDark,
} from "../InvoiceMockComponents";
import { InvoicePaper } from "../ManageInvoices/ManageInvoiceActionModals";
import {
  canEditInvoice,
  canRefundInvoice,
  formatCurrencyFromCents,
  getInvoiceTotalInCents,
  MockInvoiceLineItem,
} from "../mockInvoiceData";
import {
  dateInputToUtcMilli,
  getPaymentChoiceTypeForLabel,
  invoiceFromDetail,
  manageInvoiceRowFromRecord,
  toBackendLineItems,
} from "../invoiceBackendAdapters";
import { isValidInvoiceEmail } from "../CreateInvoice/createInvoiceHelpers";
import { downloadBase64File, downloadTextFile } from "../invoiceFileDownloads";

const shouldMarkCmpInvoiceModuleTestData = process.env.NODE_ENV === "development";

const cloneLineItems = (lineItems: MockInvoiceLineItem[]) =>
  lineItems.map((item) => ({ ...item }));

const getUpdatePath = (invoiceId: string) =>
  RoutePaths.chamberAdmin_invoicing_update.replace(":invoiceId", invoiceId);

const getStripeReturnUrl = () =>
  `${window.location.origin}${RoutePaths.chamberAdmin_invoicing_manage}`;

const UpdateInvoice = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const {
    data: chamberSettingsData,
    loading: chamberSettingsLoading,
  } = useGetChamberMemberProSettingsQuery({
    variables: { chamberId },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const {
    data: invoiceDetailData,
    loading: invoiceDetailLoading,
    error: invoiceDetailError,
    refetch,
  } = useGetCmpInvoiceDetailQuery({
    variables: {
      input: {
        chamberId,
        invoiceId: invoiceId || "",
      },
    },
    skip: !chamberId || !invoiceId,
    fetchPolicy: "cache-and-network",
  });
  const [createCmpInvoice, { loading: creatingReplacement }] =
    useCreateCmpInvoiceMutation();
  const [sendCmpInvoicePaymentRequest, { loading: sendingPaymentRequest }] =
    useSendCmpInvoicePaymentRequestMutation();
  const [resendCmpInvoicePaymentRequest, { loading: resendingPaymentRequest }] =
    useResendCmpInvoicePaymentRequestMutation();
  const [downloadCmpInvoicePdf, { loading: downloadingPdf }] =
    useDownloadCmpInvoicePdfMutation();
  const [exportCmpInvoicesAccountingCsv, { loading: exportingCsv }] =
    useExportCmpInvoicesAccountingCsvMutation();
  const [voidCmpInvoice, { loading: voidingInvoice }] = useVoidCmpInvoiceMutation();
  const [recordCmpInvoiceManualPayment, { loading: recordingManualPayment }] =
    useRecordCmpInvoiceManualPaymentMutation();
  const [startCmpInvoiceCardCheckout, { loading: startingCardCheckout }] =
    useStartCmpInvoiceCardCheckoutMutation();
  const [recordCmpInvoiceManualRefund, { loading: recordingManualRefund }] =
    useRecordCmpInvoiceManualRefundMutation();
  const [refundCmpInvoiceStripePayment, { loading: refundingStripePayment }] =
    useRefundCmpInvoiceStripePaymentMutation();
  const detail = invoiceDetailData?.getCmpInvoiceDetail;
  const creditCardPaymentsReady = getCmpCreditCardPaymentsReady(
    chamberSettingsData?.getChamberMemberProSettings.paymentsAndTaxes
      .stripeConnect
  );
  const invoice = useMemo(() => (detail ? invoiceFromDetail(detail) : null), [detail]);
  const [lineItems, setLineItems] = useState<MockInvoiceLineItem[]>([]);
  const [billingContactName, setBillingContactName] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [billingPhone, setBillingPhone] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [requestedPaymentMethodLabel, setRequestedPaymentMethodLabel] =
    useState("Invoice Me");
  const [publicMemo, setPublicMemo] = useState("");
  const [notes, setNotes] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // POL-019 (B5): per-field validation on the editable (replacement) branch,
  // mirroring the Create Invoice pattern.
  const [billingEmailError, setBillingEmailError] = useState("");
  const [lineDescriptionErrors, setLineDescriptionErrors] = useState<
    Record<string, string>
  >({});
  const [lastAction, setLastAction] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("Voided by staff from invoice operations");
  const [manualPaymentDialogOpen, setManualPaymentDialogOpen] = useState(false);
  const [manualPaymentMethodLabel, setManualPaymentMethodLabel] = useState("");
  const [manualPaymentReference, setManualPaymentReference] =
    useState("Recorded by staff");
  const [manualRefundDialogOpen, setManualRefundDialogOpen] = useState(false);
  const [manualRefundMethodLabel, setManualRefundMethodLabel] = useState("");
  const [manualRefundReason, setManualRefundReason] =
    useState("Full refund recorded by staff");
  const [stripeRefundDialogOpen, setStripeRefundDialogOpen] = useState(false);

  useEffect(() => {
    if (!invoice) {
      return;
    }

    setLineItems(cloneLineItems(invoice.lineItems));
    setBillingContactName(invoice.billingContactName);
    setBillingEmail(invoice.billingEmail);
    setBillingPhone(invoice.billingPhone);
    setBillingAddress(invoice.billingAddress);
    setDueDate(invoice.dueDate || "");
    setRequestedPaymentMethodLabel(invoice.requestedPaymentMethodLabel);
    setPublicMemo(invoice.publicMemo);
    setNotes(invoice.notes);
    setHasUnsavedChanges(false);
  }, [invoice]);

  useEffect(() => {
    if (chamberSettingsLoading || creditCardPaymentsReady) {
      return;
    }

    setRequestedPaymentMethodLabel((currentLabel) =>
      getPaymentChoiceTypeForLabel(currentLabel) ===
      CmpPaymentChoiceTypeEnum.CreditCard
        ? "Invoice Me"
        : currentLabel
    );
  }, [chamberSettingsLoading, creditCardPaymentsReady, invoice?.id]);

  const workingInvoice = useMemo(() => {
    if (!invoice) {
      return null;
    }

    return {
      ...invoice,
      billingContactName,
      billingEmail,
      billingPhone,
      billingAddress,
      dueDate,
      requestedPaymentMethodLabel,
      lineItems,
      publicMemo,
      notes,
      emailRouting: {
        ...invoice.emailRouting,
        intendedRecipientEmail: billingEmail,
      },
    };
  }, [
    billingAddress,
    billingContactName,
    billingEmail,
    billingPhone,
    dueDate,
    invoice,
    lineItems,
    notes,
    publicMemo,
    requestedPaymentMethodLabel,
  ]);

  const isEditable = workingInvoice ? canEditInvoice(workingInvoice) : false;
  const actionLoading =
    sendingPaymentRequest ||
    resendingPaymentRequest ||
    downloadingPdf ||
    exportingCsv ||
    voidingInvoice ||
    recordingManualPayment ||
    startingCardCheckout ||
    recordingManualRefund ||
    refundingStripePayment;

  if (invoiceDetailLoading && !workingInvoice) {
    return (
      <div className="mx-auto flex max-w-4xl items-center justify-center gap-3 pb-10 text-sm text-gray-600">
        <CircularProgress size={20} />
        Loading invoice detail...
      </div>
    );
  }

  if (invoiceDetailError || !workingInvoice || !detail) {
    return (
      <div className="mx-auto max-w-4xl pb-10">
        {/* POL-021 (B5): anchor navigation (RouterLink) so the shared
            UnsavedChangesPrompt click interception guards this exit. */}
        <Button
          component={RouterLink}
          to={RoutePaths.chamberAdmin_invoicing_manage}
          startIcon={<ArrowBackIcon />}
          className="mb-4 text-gray-700"
        >
          Back to Manage Invoices
        </Button>
        <Alert severity="warning">
          Invoice not found or could not be loaded.{" "}
          {invoiceDetailError ? invoiceDetailError.message : "Choose another invoice."}
        </Alert>
      </div>
    );
  }

  const canRunStripeTestRefund =
    canRefundInvoice(workingInvoice) && workingInvoice.providerMode === "Stripe test";
  const backendInvoice = detail?.invoice;
  const paymentRules = backendInvoice?.paymentRules || null;
  const paymentPlan = backendInvoice?.paymentPlan || null;
  const nextPaymentAmountInCents =
    paymentPlan?.nextInstallmentAmountInCents ||
    backendInvoice?.amountDueInCents ||
    getInvoiceTotalInCents(workingInvoice);
  const nextInstallmentId = paymentPlan?.nextInstallmentId || undefined;
  const manualPaymentMethods =
    paymentRules?.allowedManualPaymentMethodLabels || [];

  const handleEditableChange = (callback: () => void) => {
    callback();
    if (isEditable) {
      setHasUnsavedChanges(true);
    }
  };

  const handleLineItemsChange = (nextLineItems: MockInvoiceLineItem[]) => {
    // POL-019 (B5): clear description errors for removed lines and for lines
    // whose description changed.
    setLineDescriptionErrors((previous) => {
      const erroredIds = Object.keys(previous);

      if (erroredIds.length === 0) {
        return previous;
      }

      const previousById = new Map(lineItems.map((item) => [item.id, item]));
      const nextById = new Map(nextLineItems.map((item) => [item.id, item]));
      const next = { ...previous };
      let changed = false;

      erroredIds.forEach((lineItemId) => {
        const nextItem = nextById.get(lineItemId);
        const previousItem = previousById.get(lineItemId);

        if (!nextItem || (previousItem && previousItem.description !== nextItem.description)) {
          delete next[lineItemId];
          changed = true;
        }
      });

      return changed ? next : previous;
    });
    setLineItems(nextLineItems);
    if (isEditable) {
      setHasUnsavedChanges(true);
    }
  };

  const handleActionError = (error: unknown, fallbackMessage: string) => {
    setErrorMessage(error instanceof Error ? error.message : fallbackMessage);
  };

  const getActionTestFlag = () =>
    workingInvoice?.isCmpInvoiceModuleTestData ||
    shouldMarkCmpInvoiceModuleTestData ||
    undefined;

  const handleDownloadPdf = async () => {
    try {
      setErrorMessage(null);
      const result = await downloadCmpInvoicePdf({
        variables: {
          input: {
            chamberId,
            invoiceId: workingInvoice.id,
            isCmpInvoiceModuleTestData: getActionTestFlag(),
          },
        },
      });
      const download = result.data?.downloadCmpInvoicePdf.download;

      if (!download) {
        throw new Error("Invoice PDF download did not return a file.");
      }

      downloadBase64File(download);
      setLastAction(`${workingInvoice.invoiceNumber} PDF generated and downloaded.`);
      await refetch();
    } catch (error) {
      handleActionError(error, "Invoice PDF could not be generated.");
    }
  };

  const handleExportRow = async () => {
    try {
      setErrorMessage(null);
      const result = await exportCmpInvoicesAccountingCsv({
        variables: {
          input: {
            chamberId,
            profile: CmpInvoiceAccountingExportProfileEnum.GenericCsv,
            search: workingInvoice.invoiceNumber,
            isCmpInvoiceModuleTestData: getActionTestFlag(),
          },
        },
      });
      const exportResult = result.data?.exportCmpInvoicesAccountingCsv.export;

      if (!exportResult) {
        throw new Error("Invoice export did not return a CSV file.");
      }

      downloadTextFile({
        content: exportResult.csv,
        contentType: exportResult.contentType,
        filename: exportResult.filename,
      });
      setLastAction(
        `${workingInvoice.invoiceNumber} CSV exported with ${exportResult.rowCount} row(s).`
      );
      await refetch();
    } catch (error) {
      handleActionError(error, "Invoice export could not be generated.");
    }
  };

  const handleSendPaymentRequest = async () => {
    try {
      setErrorMessage(null);
      const shouldResend = workingInvoice.emailRouting.state !== "notSent";
      const outboundEmail = shouldResend
        ? (
            await resendCmpInvoicePaymentRequest({
              variables: {
                input: {
                  chamberId,
                  invoiceId: workingInvoice.id,
                  isCmpInvoiceModuleTestData: getActionTestFlag(),
                },
              },
            })
          ).data?.resendCmpInvoicePaymentRequest.outboundEmail
        : (
            await sendCmpInvoicePaymentRequest({
              variables: {
                input: {
                  chamberId,
                  invoiceId: workingInvoice.id,
                  isCmpInvoiceModuleTestData: getActionTestFlag(),
                },
              },
            })
          ).data?.sendCmpInvoicePaymentRequest.outboundEmail;

      setLastAction(
        outboundEmail
          ? `${workingInvoice.invoiceNumber} ${shouldResend ? "resend" : "send"} recorded. Intended: ${
              outboundEmail.intendedToEmail || "not available"
            }. Actual: ${outboundEmail.toEmail}. Status: ${outboundEmail.status}.`
          : `${workingInvoice.invoiceNumber} payment request ${
              shouldResend ? "resent" : "sent"
            }.`
      );
      await refetch();
    } catch (error) {
      handleActionError(error, "Invoice payment request could not be sent.");
    }
  };

  const handleVoidInvoice = async () => {
    if (!voidReason.trim()) {
      setErrorMessage("A void reason is required.");
      return;
    }

    try {
      setErrorMessage(null);
      await voidCmpInvoice({
        variables: {
          input: {
            chamberId,
            invoiceId: workingInvoice.id,
            reason: voidReason.trim(),
            isCmpInvoiceModuleTestData: getActionTestFlag(),
          },
        },
      });
      setHasUnsavedChanges(false);
      setVoidDialogOpen(false);
      setLastAction(`${workingInvoice.invoiceNumber} was voided.`);
      await refetch();
    } catch (error) {
      handleActionError(error, "Invoice could not be voided.");
    }
  };

  const handleRecordManualPayment = async () => {
    if (!manualPaymentMethodLabel.trim()) {
      setErrorMessage("A payment method label is required.");
      return;
    }

    try {
      setErrorMessage(null);
      await recordCmpInvoiceManualPayment({
        variables: {
          input: {
            chamberId,
            invoiceId: workingInvoice.id,
            installmentId: nextInstallmentId,
            amountInCents: nextPaymentAmountInCents,
            paymentMethodLabel: manualPaymentMethodLabel.trim(),
            reference: manualPaymentReference.trim() || undefined,
            isCmpInvoiceModuleTestData: getActionTestFlag(),
          },
        },
      });
      setHasUnsavedChanges(false);
      setManualPaymentDialogOpen(false);
      setLastAction(`${workingInvoice.invoiceNumber} manual payment recorded.`);
      await refetch();
    } catch (error) {
      handleActionError(error, "Manual payment could not be recorded.");
    }
  };

  const handleStartCardCheckout = async () => {
    try {
      setErrorMessage(null);
      const result = await startCmpInvoiceCardCheckout({
        variables: {
          input: {
            chamberId,
            invoiceId: workingInvoice.id,
            installmentId: nextInstallmentId,
            successUrl: getStripeReturnUrl(),
            cancelUrl: getStripeReturnUrl(),
            isCmpInvoiceModuleTestData: getActionTestFlag(),
          },
        },
      });
      const checkoutUrl = result.data?.startCmpInvoiceCardCheckout.checkoutUrl;

      if (!checkoutUrl) {
        throw new Error("Stripe checkout did not return a URL.");
      }

      window.location.assign(checkoutUrl);
    } catch (error) {
      handleActionError(error, "Stripe checkout could not be opened.");
    }
  };

  const handleRecordManualRefund = async () => {
    if (!manualRefundMethodLabel.trim()) {
      setErrorMessage("A refund method label is required.");
      return;
    }

    try {
      setErrorMessage(null);
      await recordCmpInvoiceManualRefund({
        variables: {
          input: {
            chamberId,
            invoiceId: workingInvoice.id,
            refundAmountInCents: getInvoiceTotalInCents(workingInvoice),
            paymentMethodLabel: manualRefundMethodLabel.trim(),
            reason: manualRefundReason.trim() || undefined,
            isCmpInvoiceModuleTestData: getActionTestFlag(),
          },
        },
      });
      setManualRefundDialogOpen(false);
      setLastAction(`${workingInvoice.invoiceNumber} manual full refund recorded.`);
      await refetch();
    } catch (error) {
      handleActionError(error, "Manual refund could not be recorded.");
    }
  };

  const handleStripeRefund = async () => {
    try {
      setErrorMessage(null);
      const result = await refundCmpInvoiceStripePayment({
        variables: {
          input: {
            chamberId,
            invoiceId: workingInvoice.id,
            confirmTestModeOnly: true,
            // Q-036a (B5): persisted refund reason — plain operational
            // language instead of the old test-harness string.
            reason: "Full refund issued from invoice operations (Stripe test mode).",
            isCmpInvoiceModuleTestData: getActionTestFlag(),
          },
        },
      });
      const payload = result.data?.refundCmpInvoiceStripePayment;

      if (!payload?.actionCompletedSuccessfully) {
        setStripeRefundDialogOpen(false);
        setErrorMessage(
          payload?.refund?.reason ||
            "Stripe refund attempt was recorded but did not complete."
        );
      } else {
        setStripeRefundDialogOpen(false);
        setLastAction(`${workingInvoice.invoiceNumber} Stripe full refund completed.`);
      }

      await refetch();
    } catch (error) {
      handleActionError(error, "Stripe refund could not be completed.");
    }
  };

  const handleCreateReplacement = async () => {
    // POL-019 (B5): per-field validation on the editable branch (billing
    // email required + format, per-line description) before the mutation; the
    // generic error Alert stays as the summary.
    const nextBillingEmailError = !billingEmail.trim()
      ? "Billing email is required."
      : !isValidInvoiceEmail(billingEmail)
        ? "Enter a valid billing email address."
        : "";
    const nextLineDescriptionErrors: Record<string, string> = {};

    lineItems.forEach((item) => {
      if (!item.description.trim()) {
        nextLineDescriptionErrors[item.id] =
          "Enter a description for this line.";
      }
    });

    setBillingEmailError(nextBillingEmailError);
    setLineDescriptionErrors(nextLineDescriptionErrors);

    if (nextBillingEmailError || Object.keys(nextLineDescriptionErrors).length > 0) {
      setErrorMessage("Fix the highlighted fields below and try again.");
      return;
    }

    if (
      getPaymentChoiceTypeForLabel(requestedPaymentMethodLabel) ===
        CmpPaymentChoiceTypeEnum.CreditCard &&
      !creditCardPaymentsReady
    ) {
      setErrorMessage(
        "Credit card checkout is unavailable for this chamber. Choose another requested payment method and try again."
      );
      return;
    }

    try {
      setErrorMessage(null);

      if (!chamberId) {
        throw new Error("Chamber is still loading. Try again in a moment.");
      }

      if (getInvoiceTotalInCents({ lineItems }) <= 0) {
        throw new Error(
          "Invoice total must be greater than $0.00. Use a waived or comped workflow for free outcomes."
        );
      }

      const result = await createCmpInvoice({
        variables: {
          input: {
            chamberId,
            sourceKind:
              detail.invoice.sourceKind || CmpOperationalSourceKindEnum.CustomInvoice,
            chamberMemberId: detail.invoice.chamberMemberId || undefined,
            eventId: detail.invoice.eventId || undefined,
            eventRegistrationId: detail.invoice.eventRegistrationId || undefined,
            eventSponsorshipRequestId:
              detail.invoice.eventSponsorshipRequestId || undefined,
            billingSnapshot: {
              customerName: workingInvoice.customerName,
              contactName: billingContactName || undefined,
              contactEmail: billingEmail,
              contactPhone: billingPhone || undefined,
              addressLine1: billingAddress || undefined,
              country: "Canada",
            },
            lineItems: toBackendLineItems(lineItems),
            paymentChoice: {
              type: getPaymentChoiceTypeForLabel(requestedPaymentMethodLabel),
              manualPaymentMethodId:
                detail.invoice.paymentChoice.manualPaymentMethodId || undefined,
              autoRenew: detail.invoice.paymentChoice.autoRenew,
              emailCommunicationConsentConfirmed:
                detail.invoice.paymentChoice.emailCommunicationConsentConfirmed,
            },
            dueAtUtcMilli: dateInputToUtcMilli(dueDate),
            publicMemo: publicMemo || undefined,
            internalNotes: notes || undefined,
            replacesInvoiceId: detail.invoice.id,
            isCmpInvoiceModuleTestData: shouldMarkCmpInvoiceModuleTestData || undefined,
          },
        },
      });
      const replacementId = result.data?.createCmpInvoice.invoice?.id;

      setHasUnsavedChanges(false);
      setLastAction("Replacement invoice saved. No email was sent.");
      await refetch();

      if (replacementId) {
        navigate(getUpdatePath(replacementId));
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Replacement invoice could not be created."
      );
    }
  };

  return (
    // B5 form-system normalization (Q-034 sanctioned): page chrome via the
    // shared FormPageShell — max-w-7xl (1280px) converges on the system's
    // 1180px clamp. The alerts slot carries the page's alert stack (they now
    // render above the back-button row); the sticky in-column action band
    // stays INSIDE children because the shell's static bottom actionBar
    // cannot express a sticky band scoped to the left grid column.
    <FormPageShell
      headingId="update-invoice-detail"
      heading={isEditable ? "Update Invoice" : "View Invoice"}
      contentClassName=""
      beforeMain={<UnsavedChangesPrompt when={hasUnsavedChanges} />}
      alerts={
        <>
          <InvoiceReadinessAlert />
          <InvoiceStateNotice invoice={workingInvoice} />

          {errorMessage ? (
            <Alert
              severity="error"
              className="mb-5"
              onClose={() => setErrorMessage(null)}
            >
              {errorMessage}
            </Alert>
          ) : null}

          {lastAction ? (
            <Alert
              severity="success"
              className="mb-5"
              onClose={() => setLastAction(null)}
            >
              {lastAction}
            </Alert>
          ) : null}
        </>
      }
    >
      <div className="mb-5 flex flex-col justify-between gap-4 lg:flex-row lg:items-start">
        <div>
          {/* POL-021 (B5): anchor navigation (RouterLink) instead of
              programmatic navigate() so a dirty editable invoice gets the
              UnsavedChangesPrompt guard on the page's own primary exit. */}
          <Button
            component={RouterLink}
            to={RoutePaths.chamberAdmin_invoicing_manage}
            startIcon={<ArrowBackIcon />}
            className="mb-3 text-gray-700"
          >
            Back to Manage Invoices
          </Button>
        </div>
        <div className="rounded border border-gray-200 bg-white px-4 py-3 text-sm shadow-sm">
          <p className="font-bold text-gray-950">Invoice total</p>
          <p className="mt-1 text-xl font-bold text-gray-950">
            {formatCurrencyFromCents(getInvoiceTotalInCents(workingInvoice))}
          </p>
        </div>
      </div>

      <div className="mb-5 rounded border border-gray-200 bg-white p-5 shadow-sm">
        <p
          className="inline-flex rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide"
          style={{ backgroundColor: `${cpaGold}26`, color: cpaGoldDark }}
        >
          {isEditable ? "Update Invoice" : "View Invoice"}
        </p>
        <p className="mt-3 text-lg font-bold text-gray-950">
          {workingInvoice.invoiceNumber} for {workingInvoice.customerName}
        </p>
        <p className="mt-1 text-sm text-gray-600">
          {isEditable
            ? "Unpaid invoices can be changed by voiding the original and creating a replacement invoice."
            : "This invoice is read-only. Staff can use safe payment, refund, download, and export actions where the state allows them."}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_460px]">
        <div className="space-y-5">
          <InvoiceSection
            label="State"
            title={isEditable ? "Unpaid Invoice Reissue Flow" : "Read-Only Invoice State"}
            description={
              isEditable
                ? "Saving a replacement creates a new CAP invoice, voids this original unpaid invoice, and keeps both records in history."
                : "Draft, paid, refunded, voided, waived, failed, abandoned, expired, and action-required invoices are not editable."
            }
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <TextField label="Invoice Number" value={workingInvoice.invoiceNumber} disabled />
              <TextField label="Customer" value={workingInvoice.customerName} disabled />
              <TextField label="Source" value={workingInvoice.sourceLabel} disabled />
              <TextField
                label="Actual Payment Method"
                value={workingInvoice.paymentMethodLabel}
                disabled
              />
              <TextField
                label="Provider / Mode"
                value={workingInvoice.providerMode || "CAP operational record"}
                disabled
              />
              <TextField
                label="Amount"
                value={formatCurrencyFromCents(getInvoiceTotalInCents(workingInvoice))}
                disabled
              />
            </div>
          </InvoiceSection>

          <InvoiceSection
            label="Billing"
            title="Billing Contact Snapshot"
            description={
              isEditable
                ? "Editable for the replacement invoice. The original invoice snapshot remains preserved."
                : "Read-only historical billing snapshot."
            }
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <TextField
                label="Billing Contact"
                value={billingContactName}
                disabled={!isEditable}
                onChange={({ target }) =>
                  handleEditableChange(() => setBillingContactName(target.value))
                }
                fullWidth
              />
              <TextField
                label="Billing Email"
                value={billingEmail}
                disabled={!isEditable}
                error={Boolean(billingEmailError)}
                helperText={billingEmailError || undefined}
                onChange={({ target }) =>
                  handleEditableChange(() => {
                    setBillingEmail(target.value);
                    setBillingEmailError("");
                  })
                }
                fullWidth
              />
              <TextField
                label="Billing Phone"
                value={billingPhone}
                disabled={!isEditable}
                onChange={({ target }) =>
                  handleEditableChange(() => setBillingPhone(target.value))
                }
                fullWidth
              />
              <TextField
                label="Due Date"
                type="date"
                value={dueDate}
                disabled={!isEditable}
                onChange={({ target }) =>
                  handleEditableChange(() => setDueDate(target.value))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Billing Address"
                value={billingAddress}
                disabled={!isEditable}
                onChange={({ target }) =>
                  handleEditableChange(() => setBillingAddress(target.value))
                }
                fullWidth
                multiline
                minRows={2}
                className="md:col-span-2"
              />
              <div className="md:col-span-2">
                <InvoicePaymentMethodSelect
                  value={requestedPaymentMethodLabel}
                  disabled={!isEditable || chamberSettingsLoading}
                  creditCardPaymentsReady={creditCardPaymentsReady}
                  onChange={(value) =>
                    handleEditableChange(() => setRequestedPaymentMethodLabel(value))
                  }
                />
              </div>
            </div>
          </InvoiceSection>

          <InvoiceSection label="Items" title="Invoice Line Items">
            <InvoiceLineItemsEditor
              lineItems={lineItems}
              onChange={handleLineItemsChange}
              readOnly={!isEditable}
              descriptionErrors={lineDescriptionErrors}
            />
            <div className="mt-5">
              <InvoiceTotals invoice={workingInvoice} />
            </div>
          </InvoiceSection>

          <InvoiceSection label="Notes" title="Memo And Staff Notes">
            <div className="grid grid-cols-1 gap-4">
              <TextField
                label="Customer Memo"
                value={publicMemo}
                disabled={!isEditable}
                onChange={({ target }) =>
                  handleEditableChange(() => setPublicMemo(target.value))
                }
                multiline
                minRows={2}
                fullWidth
              />
              <TextField
                label="Internal Notes"
                value={notes}
                disabled={!isEditable}
                onChange={({ target }) =>
                  handleEditableChange(() => setNotes(target.value))
                }
                multiline
                minRows={3}
                fullWidth
              />
            </div>
          </InvoiceSection>

          <InvoiceSection label="History" title="Audit / Support History">
            <InvoiceHistory invoice={workingInvoice} />
          </InvoiceSection>

          <div className="sticky bottom-0 z-10 -mx-2 border-t border-gray-200 bg-white/95 px-2 py-4 backdrop-blur">
            {isEditable ? (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
                <Button
                  variant="outlined"
                  startIcon={<DownloadOutlinedIcon />}
                  disabled={actionLoading}
                  onClick={handleDownloadPdf}
                >
                  Download PDF
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EmailOutlinedIcon />}
                  disabled={actionLoading}
                  onClick={handleSendPaymentRequest}
                >
                  {workingInvoice.emailRouting.state === "notSent" ? "Send" : "Resend"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PaymentsOutlinedIcon />}
                  disabled={actionLoading}
                  onClick={() => {
                    setManualPaymentMethodLabel(
                      manualPaymentMethods[0] ||
                        (workingInvoice.requestedPaymentMethodLabel ===
                        "Credit card checkout"
                          ? "Manual payment"
                          : workingInvoice.requestedPaymentMethodLabel)
                    );
                    setManualPaymentReference("Recorded by staff");
                    setManualPaymentDialogOpen(true);
                  }}
                >
                  Process Payment
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadOutlinedIcon />}
                  disabled={actionLoading}
                  onClick={handleExportRow}
                >
                  Export Row
                </Button>
                <Button
                  variant="contained"
                  startIcon={
                    creatingReplacement || actionLoading ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : (
                      <SaveOutlinedIcon />
                    )
                  }
                  disabled={creatingReplacement || actionLoading}
                  onClick={handleCreateReplacement}
                  sx={{
                    backgroundColor: cpaGold,
                    "&:hover": { backgroundColor: cpaGoldDark },
                  }}
                >
                  Void Original And Create Replacement
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={actionLoading || creatingReplacement}
                  onClick={() => {
                    setVoidReason("Voided by staff from invoice operations");
                    setVoidDialogOpen(true);
                  }}
                  className="md:col-span-5"
                >
                  Void Without Replacement
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <Button
                  variant="outlined"
                  startIcon={<DownloadOutlinedIcon />}
                  disabled={actionLoading}
                  onClick={handleDownloadPdf}
                >
                  Download PDF
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadOutlinedIcon />}
                  disabled={actionLoading}
                  onClick={handleExportRow}
                >
                  Export Row
                </Button>
                <Button
                  variant="outlined"
                  disabled={!canRefundInvoice(workingInvoice) || actionLoading}
                  startIcon={<PaymentsOutlinedIcon />}
                  onClick={() => {
                    setManualRefundMethodLabel(
                      workingInvoice.paymentMethodLabel === "Not paid"
                        ? "External refund"
                        : workingInvoice.paymentMethodLabel
                    );
                    setManualRefundReason("Full refund recorded by staff");
                    setManualRefundDialogOpen(true);
                  }}
                >
                  Manual Refund
                </Button>
                <Button
                  variant="contained"
                  disabled={!canRunStripeTestRefund || actionLoading}
                  startIcon={
                    refundingStripePayment ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : (
                      <PaymentsOutlinedIcon />
                    )
                  }
                  onClick={() => setStripeRefundDialogOpen(true)}
                  sx={{
                    backgroundColor: cpaGold,
                    "&:hover": { backgroundColor: cpaGoldDark },
                  }}
                >
                  Stripe Test Refund
                </Button>
              </div>
            )}
          </div>
        </div>

        <aside className="space-y-5">
          <InvoiceEmailRoutingPanel
            invoice={workingInvoice}
            title="Invoice Email Routing"
          />
          {/* Q-036d (B5): the mock InvoicePreview (hardcoded "Sand Bay
              Chamber of Commerce" identity) is replaced by the shared
              InvoicePaper master document fed from the live detail fragment;
              the row shape is synthesized via manageInvoiceRowFromRecord
              (detail.invoice selects the same CmpInvoiceRecordFields). The
              aside now reflects the persisted invoice record, not in-flight
              replacement edits. */}
          <InvoicePaper
            chamber={chamber}
            detail={detail}
            row={manageInvoiceRowFromRecord(detail.invoice)}
          />
        </aside>
      </div>

      <Dialog
        open={manualPaymentDialogOpen}
        onClose={() => setManualPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Process Payment</DialogTitle>
        <DialogContent className="space-y-4 pt-2">
        {errorMessage ? (
          <Alert severity="error" onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        ) : null}

          <Alert severity="info">
            {paymentRules?.summary ||
              "Payments follow the enabled methods configured in Chamber Setup."}
          </Alert>
          {paymentRules?.creditCardAllowed ? (
            <div className="rounded border border-gray-200 p-3">
              <p className="mb-3 text-sm text-gray-700">
                Hosted Stripe Checkout will open. CMP will only mark this invoice
                paid after Stripe confirms the payment through the webhook.
              </p>
              <Button
                variant="contained"
                startIcon={<PaymentsOutlinedIcon />}
                disabled={startingCardCheckout}
                onClick={handleStartCardCheckout}
                sx={{
                  backgroundColor: cpaGold,
                  "&:hover": { backgroundColor: cpaGoldDark },
                }}
              >
                Continue to Stripe Checkout
              </Button>
            </div>
          ) : null}
          {paymentRules?.manualPaymentAllowed ? (
            <>
              <TextField
                select
                SelectProps={{ native: true }}
                label="Payment Method"
                value={manualPaymentMethodLabel}
                onChange={({ target }) => setManualPaymentMethodLabel(target.value)}
                fullWidth
              >
                {manualPaymentMethods.map((method) => (
                  <option key={method} value={method}>
                    {method}
                  </option>
                ))}
              </TextField>
              <TextField
                label="Reference"
                value={manualPaymentReference}
                onChange={({ target }) => setManualPaymentReference(target.value)}
                fullWidth
              />
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualPaymentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              recordingManualPayment ||
              !paymentRules?.manualPaymentAllowed ||
              !manualPaymentMethodLabel.trim()
            }
            onClick={handleRecordManualPayment}
            sx={{ backgroundColor: cpaGold, "&:hover": { backgroundColor: cpaGoldDark } }}
          >
            Confirm and record payment
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={manualRefundDialogOpen}
        onClose={() => setManualRefundDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Record Manual Refund</DialogTitle>
        <DialogContent className="space-y-4 pt-2">
        {errorMessage ? (
          <Alert severity="error" onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        ) : null}

          <Alert severity="info">
            Records full refunds only. Use this for non-Stripe or externally
            completed refunds.
          </Alert>
          <TextField
            label="Refund Method"
            value={manualRefundMethodLabel}
            onChange={({ target }) => setManualRefundMethodLabel(target.value)}
            fullWidth
          />
          <TextField
            label="Reason"
            value={manualRefundReason}
            onChange={({ target }) => setManualRefundReason(target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualRefundDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={recordingManualRefund}
            onClick={handleRecordManualRefund}
            sx={{ backgroundColor: cpaGold, "&:hover": { backgroundColor: cpaGoldDark } }}
          >
            Record Refund
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={voidDialogOpen} onClose={() => setVoidDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Void Invoice</DialogTitle>
        <DialogContent className="space-y-4 pt-2">
        {errorMessage ? (
          <Alert severity="error" onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        ) : null}

          <Alert severity="warning">
            This voids the unpaid invoice and preserves the original record in history.
          </Alert>
          <TextField
            label="Void Reason"
            value={voidReason}
            onChange={({ target }) => setVoidReason(target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={voidingInvoice}
            onClick={handleVoidInvoice}
          >
            Void Invoice
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={stripeRefundDialogOpen}
        onClose={() => setStripeRefundDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Stripe Test Refund</DialogTitle>
        <DialogContent className="space-y-4 pt-2">
        {errorMessage ? (
          <Alert severity="error" onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        ) : null}

          <Alert severity="warning">
            This runs a full refund through the Stripe test-mode contract. Live refunds
            remain configuration-gated and are not executed locally.
          </Alert>
          <p className="text-sm text-gray-700">
            Invoice {workingInvoice.invoiceNumber} will be refunded for{" "}
            {formatCurrencyFromCents(getInvoiceTotalInCents(workingInvoice))}.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStripeRefundDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={refundingStripePayment}
            onClick={handleStripeRefund}
            sx={{ backgroundColor: cpaGold, "&:hover": { backgroundColor: cpaGoldDark } }}
          >
            Run Test Refund
          </Button>
        </DialogActions>
      </Dialog>
    </FormPageShell>
  );
};

export default UpdateInvoice;
